// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import { AccordionSectionPanelProps } from "@insite/mobius/AccordionSection/AccordionSectionPanel";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

interface SectionModel {
    fields: {
        sectionTitle: string;
        sectionContent: string;
        isOpen: boolean;
    };
}

const enum fields {
    accordionSections = "accordionSections",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.accordionSections]: SectionModel[];
    };
}

type Props = OwnProps;

export interface ContentAccordionStyles {
    specificationsAccordion?: AccordionProps;
    sectionStyles?: AccordionSectionPanelProps;
    contentText?: TypographyPresentationProps;
    accordionSectionHeading?: AccordionSectionHeaderProps;
    accordionSectionHeadingTitle?: TypographyPresentationProps;
    toggleIcon?: IconPresentationProps;
}

export const contentAccordionStyles: ContentAccordionStyles = {
    sectionStyles: {
        css: css`
            background-color: ${getColor("primary")};
            border: none;
            padding: 1em 1.4em;
        `,
    },
    contentText: {
        color: "primary.contrast",
    },
    accordionSectionHeading: {
        css: css`
            padding-top: ${pxToRem(5)};
            button {
                padding: 1em 1.4em;
                background: ${(props: AccordionSectionHeaderProps) =>
                    props.expanded ? getColor("secondary") : getColor("border.light")};
                font-size: ${pxToRem(16)};
                border: none;
                color: ${(props: AccordionSectionHeaderProps) =>
                    props.expanded ? getColor("primary.contrast") : getColor("text.main")};
                &:focus {
                    padding: 1em 1.4em;
                    border: ${pxToRem(0)};
                    outline: rgb(0, 153, 255) solid ${pxToRem(2)};
                    outline-offset: -${pxToRem(2)};
                }
            }
        `,
    },
    accordionSectionHeadingTitle: {
        css: css`
            text-transform: uppercase;
        `,
    },
    toggleIcon: {
        color: "primary",
    },
};

const styles = contentAccordionStyles;

const ContentAccordion: FC<Props> = ({ fields: { accordionSections } }) => {
    const [accordionRows, setAccordionRows] = useState<SectionModel[]>();

    useEffect(() => {
        const newRows: SectionModel[] = accordionSections.map(accordionSection => ({
            fields: {
                ...accordionSection.fields,
            },
        }));
        setAccordionRows(newRows);
    }, [accordionSections]);

    const onToggleHandler = (expanded: boolean, title: string) => {
        const newRows: SectionModel[] = accordionSections.map(accordionSection => {
            if (accordionSection.fields.sectionTitle === title) {
                return {
                    fields: {
                        ...accordionSection.fields,
                        isOpen: !expanded,
                    },
                };
            }

            return accordionSection;
        });
        setAccordionRows(newRows);
    };

    if (!accordionRows) {
        return null;
    }

    return (
        <>
            <Accordion headingLevel={2}>
                {accordionRows?.map(({ fields }, index) => {
                    const rowId = `${fields.sectionTitle}-${index}`;
                    const key = `${fields.sectionTitle}-${index}`;
                    return (
                        <StyledWrapper key={key} id={rowId}>
                            <AccordionSection
                                title={fields.sectionTitle}
                                uid={fields.sectionTitle}
                                panelProps={styles.sectionStyles}
                                headerProps={styles.accordionSectionHeading}
                                toggleIconProps={styles.toggleIcon}
                                titleTypographyProps={styles.accordionSectionHeadingTitle}
                                onTogglePanel={expanded => onToggleHandler(expanded, fields.sectionTitle)}
                                expanded={fields.isOpen}
                            >
                                <Typography {...styles.contentText}>
                                    {parse(fields.sectionContent, parserOptions)}
                                </Typography>
                            </AccordionSection>
                        </StyledWrapper>
                    );
                })}
            </Accordion>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "list-dropdown",
    fieldDefinitions: [
        {
            name: fields.accordionSections,
            editorTemplate: "ListField",
            getDisplay: ({ fields: { sectionTitle } }) => sectionTitle,
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "sectionTitle",
                    displayName: "Section Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "sectionContent",
                    displayName: "Content",
                    editorTemplate: "RichTextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "isOpen",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: () => false,
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ContentAccordion,
    definition,
};

export default widgetModule;
